import { type StepForm, WizardField } from '../WizardField';

interface Props {
  form: StepForm;
}

const APPROVAL_GATE_NOTE: Record<string, string> = {
  school_board: 'This package requires school-board approval (e.g. OH abatement over 50%).',
  referendum: 'This package requires a voter referendum (e.g. FL ad valorem exemption).',
  state_review: 'This package requires state review/approval (e.g. TN term over 20 years).',
};

export function Step8Incentives({ form }: Props) {
  const approvalGate = form.watch('step8.approvalGate') as string | undefined;

  // Stepped/declining abatement schedule — an optional per-year override of
  // the flat propertyTaxAbatementPct/Years pair (real-world deals often look
  // like 100/100/80/80/60/60/40/40/20/20 rather than a flat percentage).
  const horizonRaw = form.watch('step1.analysisPeriodYears') as number | undefined;
  const horizon = Number.isFinite(horizonRaw) && (horizonRaw ?? 0) > 0 ? Number(horizonRaw) : 10;
  const schedule = form.watch('step8.propertyTaxAbatementSchedule') as number[] | undefined;
  const useStepped = Array.isArray(schedule) && schedule.length > 0;

  const toggleStepped = (checked: boolean) => {
    if (checked) {
      const flatPct = (form.getValues('step8.propertyTaxAbatementPct') as number | undefined) ?? 0;
      const flatYears =
        (form.getValues('step8.propertyTaxAbatementYears') as number | undefined) ?? 0;
      const prefill = Array.from({ length: horizon }, (_, i) => (i < flatYears ? flatPct : 0));
      form.setValue('step8.propertyTaxAbatementSchedule', prefill, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      form.setValue('step8.propertyTaxAbatementSchedule', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Incentive Package</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          City incentives offered to the project. Leave all fields at 0 if the city is offering no
          incentives — this gives a clean baseline for comparison.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Job creation grant
        </p>

        <WizardField
          form={form}
          name="step8.jobGrantPctOfPayroll"
          label="Grant as % of annual payroll"
          format="percent"
          min={0}
          max={1}
          step={0.01}
          hint="Paid annually for the duration of the grant"
        />

        <WizardField
          form={form}
          name="step8.jobGrantYears"
          label="Grant duration (years)"
          type="number"
          min={0}
          max={30}
          step={1}
        />

        {/* ADR-035 3.3: grant base */}
        <WizardField
          form={form}
          name="step8.jobGrantBase"
          label="Grant base"
          type="select"
          options={[
            { value: 'payroll', label: 'Percent of payroll' },
            { value: 'withholding', label: 'Percent of income-tax withholdings (NC JDIG)' },
          ]}
          hint="Withholding base multiplies payroll by the effective withholding rate"
        />

        <WizardField
          form={form}
          name="step8.effectiveWithholdingRate"
          label="Effective withholding rate"
          format="percent"
          min={0}
          max={1}
          step={0.001}
          hint="Only used when the grant base is withholdings"
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Infrastructure grant
        </p>

        <WizardField
          form={form}
          name="step8.infrastructureGrantTotal"
          label="Total infrastructure grant"
          format="currency"
        />

        <WizardField
          form={form}
          name="step8.infrastructureGrantYears"
          label="Grant distributed over (years)"
          type="number"
          min={0}
          max={30}
          step={1}
        />

        <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Property tax abatement
        </p>

        <div className="col-span-2 flex items-center gap-2">
          <input
            id="step8-use-stepped-schedule"
            type="checkbox"
            checked={useStepped}
            onChange={(e) => toggleStepped(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
          />
          <label htmlFor="step8-use-stepped-schedule" className="text-sm text-slate-700">
            Use stepped/declining schedule instead of a flat percentage
          </label>
        </div>

        {useStepped ? (
          <div className="col-span-2 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {Array.from({ length: horizon }, (_, i) => (
              <WizardField
                // biome-ignore lint/suspicious/noArrayIndexKey: year index is the stable schedule position
                key={i}
                form={form}
                name={`step8.propertyTaxAbatementSchedule.${i}`}
                label={`Year ${i + 1}`}
                format="percent"
                min={0}
                max={1}
                step={0.05}
              />
            ))}
            <p className="col-span-2 sm:col-span-5 text-xs text-slate-400">
              e.g. 100/100/80/80/60/60/40/40/20/20% over a 10-year term. Overrides the flat fraction
              and duration below.
            </p>
          </div>
        ) : (
          <>
            <WizardField
              form={form}
              name="step8.propertyTaxAbatementPct"
              label="Abatement fraction"
              format="percent"
              min={0}
              max={1}
              step={0.05}
              hint="Percentage of city property tax abated"
            />

            <WizardField
              form={form}
              name="step8.propertyTaxAbatementYears"
              label="Abatement duration (years)"
              type="number"
              min={0}
              max={30}
              step={1}
            />
          </>
        )}

        <div className="col-span-2">
          <WizardField
            form={form}
            name="step8.abatementBase"
            label="Abatement base"
            type="select"
            options={[
              { value: 'increment', label: 'Increment / net-new value (TX Ch. 312)' },
              { value: 'full', label: 'Full assessed value (project + baseline)' },
            ]}
            hint="Full value only differs from increment on an expansion with a baseline"
          />
        </div>

        {/* ADR-035 3.1: PILOT / leaseback */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          PILOT / leaseback
        </p>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          For TN IDB / GA bond-lease deals: an effective property-tax abatement that applies across
          the city, county, and ISD levies. Use this OR the standard abatement, not both.
        </p>

        <WizardField
          form={form}
          name="step8.pilotAbatementPct"
          label="PILOT effective abatement"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Share of real-property tax abated across all tiers"
        />

        <WizardField
          form={form}
          name="step8.pilotAbatementYears"
          label="PILOT duration (years)"
          type="number"
          min={0}
          max={40}
          step={1}
        />

        {/* ADR-035 3.4: sales-tax rebate */}
        <WizardField
          form={form}
          name="step8.salesTaxRebatePct"
          label="Sales-tax rebate fraction"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Share of the project's city sales tax rebated to the company"
        />

        <WizardField
          form={form}
          name="step8.salesTaxRebateYears"
          label="Sales-tax rebate duration (years)"
          type="number"
          min={0}
          max={30}
          step={1}
        />

        {/* ADR-035 simplified TIF (memo) */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tax increment financing (TIF)
        </p>
        <p className="col-span-2 -mt-2 text-xs text-slate-400">
          Memo only: the captured increment is reported, not deducted from net benefit (the company
          still pays; the district earmarks).
        </p>

        <WizardField
          form={form}
          name="step8.tifCaptureShare"
          label="TIF capture share"
          format="percent"
          min={0}
          max={1}
          step={0.05}
          hint="Share of the incremental property tax captured into a TIF fund"
        />

        <WizardField
          form={form}
          name="step8.tifYears"
          label="TIF capture duration (years)"
          type="number"
          min={0}
          max={40}
          step={1}
        />

        {/* ADR-035 3.5: AZ GPLET (effective real-property rate override) */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          GPLET (Arizona)
        </p>
        <div className="col-span-2">
          <WizardField
            form={form}
            name="step8.gpletEffectivePropertyRate"
            label="GPLET effective property rate"
            format="percent"
            min={0}
            max={0.1}
            step={0.001}
            hint="AZ only. Leave blank unless GPLET replaces ad valorem with a $/sq-ft excise; applies to the city real-property line."
          />
        </div>

        {/* ADR-035 3.6: statutory approval gate (metadata) */}
        <p className="col-span-2 mt-2 border-t border-slate-200 pt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Approval requirement
        </p>
        <div className="col-span-2">
          <WizardField
            form={form}
            name="step8.approvalGate"
            label="Statutory approval gate"
            type="select"
            options={[
              { value: 'none', label: 'None' },
              { value: 'school_board', label: 'School-board approval' },
              { value: 'referendum', label: 'Voter referendum' },
              { value: 'state_review', label: 'State review / approval' },
            ]}
          />
          {approvalGate && approvalGate !== 'none' && (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {APPROVAL_GATE_NOTE[approvalGate]}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
